import React, { useRef } from 'react';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { Colors } from '../../config';
import { libraries } from '../GoogleMap';

// Google's `route` component already bundles the street type into the name
// (e.g. "Main Street"), so streetType is intentionally left for the user to
// pick manually rather than guessed from free text.
const parseAddressComponents = (components = []) => {
    const get = (type) => components.find((c) => c.types.includes(type));
    return {
        streetNumber: get('street_number')?.long_name || '',
        streetName: get('route')?.long_name || '',
        city:
            get('locality')?.long_name ||
            get('sublocality')?.long_name ||
            get('administrative_area_level_2')?.long_name ||
            '',
        state: get('administrative_area_level_1')?.short_name || '',
        zip: get('postal_code')?.long_name || '',
    };
};

const AddressAutocomplete = ({
    onPlaceSelected,
    placeholder = 'Start searching for an address...',
    inputClass = '',
}) => {
    const autocompleteRef = useRef(null);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
        libraries,
    });

    const handlePlaceChanged = () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.geometry?.location) return;

        onPlaceSelected({
            ...parseAddressComponents(place.address_components),
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
            formattedAddress: place.formatted_address,
        });
    };

    if (!isLoaded) {
        return (
            <input
                disabled
                placeholder="Loading address search..."
                className={`pl-2.5 outline-none ${inputClass}`}
                style={{ background: Colors.LightGray }}
            />
        );
    }

    return (
        <Autocomplete
            onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
            onPlaceChanged={handlePlaceChanged}
        >
            <input
                type="text"
                placeholder={placeholder}
                className={`pl-2.5 outline-none ${inputClass}`}
                style={{ background: Colors.LightGray }}
            />
        </Autocomplete>
    );
};

export default AddressAutocomplete;
